import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { clientCreateAPISchema } from '@hero/api';
import { CONFIG, type ClientRole } from '@hero/core';
import { Client } from '@hero/schema';

/**
 * Client form values. The unified `Client` CRM entity (relations-v2)
 * carries a `name` and `roles: PartyRole[]` (multi-valued). Person
 * fields belong to the initial primary contact and are only used in
 * create mode.
 */
export type ClientFormValues = z.infer<typeof clientCreateAPISchema>;

/** Edit schema: only clientName + roles (person fields are contact-only). */
const clientEditSchema = clientCreateAPISchema.omit({
  personName: true,
  personPhone: true,
  personEmail: true,
});

type ClientEditFormValues = z.infer<typeof clientEditSchema>;

export interface UseClientFormOptions {
  defaultValues?: Partial<ClientFormValues>;
  onSubmit?: (values: ClientFormValues) => void | Promise<void>;
  /** When true, uses the edit schema (clientName + roles only). */
  isEdit?: boolean;
}

export type UseClientFormHook = ReturnType<typeof useClientForm>;

export function useClientForm(options: UseClientFormOptions = {}) {
  const { defaultValues: defaultValuesProp, onSubmit, isEdit = false } = options;

  function clientToFormValues(client: Client): Partial<ClientFormValues> {
    return {
      clientName: client.name,
      // Existing clients always carry roles; fall back to an empty list
      // for safety so we never silently inject a default like
      // `business_customer`.
      roles: client.roles?.length ? [...client.roles] : [],
      linkedParty: client.linkedParty ?? null,
    };
  }

  const getFormValues = (values: Partial<ClientFormValues> | undefined) => {
    if (isEdit) {
      return {
        clientName: values?.clientName ?? '',
        roles: values?.roles ?? [],
        linkedParty: values?.linkedParty ?? null,
      } satisfies ClientEditFormValues;
    }
    return {
      clientName: values?.clientName ?? '',
      personName: values?.personName ?? '',
      personPhone: values?.personPhone ?? '',
      personEmail: values?.personEmail ?? '',
      // Create form starts blank — the user must pick at least one role.
      // `isFormValid` blocks submit until they do.
      roles: values?.roles ?? [],
      linkedParty: values?.linkedParty ?? null,
    } satisfies ClientFormValues;
  };

  const form = useForm<ClientFormValues>({
    resolver: zodResolver(isEdit ? clientEditSchema : clientCreateAPISchema),
    defaultValues: getFormValues(defaultValuesProp),
  });

  const values = form.watch();

  /**
   * For "personal" Clients (e.g. roles = `[individual_customer]`) the
   * separate client-name input is redundant — the human's
   * `personName` IS the client name. Mirror it through to keep the
   * required `clientName` field valid while the UI hides it.
   *
   * Edit mode never touches person fields, so we skip the mirror
   * there and let the user edit the existing `clientName` directly.
   */
  const isPersonalClient = CONFIG.client.isPersonalClient(values.roles as ClientRole[]);
  const personNameForMirror = isEdit ? undefined : values.personName;
  useEffect(() => {
    if (isEdit) return;
    if (!isPersonalClient) return;
    const next = (personNameForMirror ?? '').trim();
    if (next === values.clientName) return;
    form.setValue('clientName', next, { shouldValidate: true, shouldDirty: true });
  }, [isEdit, isPersonalClient, personNameForMirror, values.clientName, form]);

  const reset = useCallback(
    (vals?: Partial<ClientFormValues>) => {
      form.reset(getFormValues(vals));
    },
    [form],
  );

  const onValidSubmit = useCallback(
    (vals: ClientFormValues) => {
      onSubmit?.(vals);
    },
    [onSubmit],
  );

  const isFormValid = useMemo(() => {
    const baseValid = form.formState.isValid;
    const hasRole = (values.roles?.length ?? 0) > 0;
    const hasName = !!values.clientName?.trim();
    return baseValid && hasRole && hasName;
  }, [form.formState.isValid, values.roles, values.clientName]);

  const setClient = useCallback(
    (client: Client) => {
      form.reset(getFormValues(clientToFormValues(client)));
    },
    [form],
  );

  return {
    form,
    values,
    isFormValid,
    isPersonalClient,
    reset,
    onValidSubmit,
    setValue: form.setValue,
    setClient,
  };
}
